import { Component } from 'preact';
import {
  DIALOG_STYLE, TEXT_PRIMARY, TEXT_BODY,
  GOLD_BTN, BEVELED_BTN, DANGER_BTN, FourCorners,
} from '../lib/medievalTheme';
import { pushDismiss, removeDismiss } from '../utils/escStack';
import { t } from '../localization/t';

// One radius for both action buttons, a step tighter than the panel's
// 0.75cqw so the pair reads as nested inside it rather than as two
// unrelated chips.
const ACTION_RADIUS = 'var(--radius-lg)';

/**
 * Themed confirmation dialog. Replaces `window.confirm()`. Native
 * OS popups break the in-game vibe and the zoom-scale layout. Driven
 * by caller state (open / close + onConfirm callback); usually
 * surfaced via a single app-level `pendingConfirm` slot.
 *
 * Props:
 *   title. Heading text (defaults to "Are you sure?")
 *   message. Body text
 *   confirmLabel. Primary button label (defaults to "Confirm")
 *   cancelLabel. Secondary button label (defaults to "Cancel")
 *   danger. If true, primary button uses DANGER_BTN
 *   onConfirm. Fired on primary click
 *   onCancel. Fired on secondary click / backdrop / Escape
 */
export default class ConfirmModal extends Component {
  componentDidMount() {
    window.addEventListener('keydown', this.handleKeyDown);
    this._escDismissId = pushDismiss(() => this.props.onCancel?.());
  }

  componentWillUnmount() {
    window.removeEventListener('keydown', this.handleKeyDown);
    removeDismiss(this._escDismissId);
  }

  handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      this.props.onConfirm?.();
    }
  };

  render() {
    const {
      title = t('Are you sure?'),
      message,
      confirmLabel = t('Confirm'),
      cancelLabel = t('Cancel'),
      danger = false,
      onConfirm,
      onCancel,
    } = this.props;

    return (
      <div
        className="fixed inset-0 z-[150] flex items-center justify-center"
        style={{ background: 'rgba(0,0,0,0.75)' }}
        onClick={onCancel}
      >
        <div
          className="relative w-[27.5cqw] flex flex-col p-6 gap-3"
          style={DIALOG_STYLE}
          onClick={(e) => e.stopPropagation()}
        >
          <FourCorners />
          <h2
            className="arena-heading text-xl font-bold text-center text-balance"
            style={{ color: TEXT_PRIMARY }}
          >
            {title}
          </h2>
          {message ? (
            <p
              className="text-sm text-center leading-relaxed text-pretty"
              style={{ color: TEXT_BODY }}
            >
              {message}
            </p>
          ) : null}
          {/* Action row. Both buttons share one geometry so they read
              as a pair: same padding, same min-width, and one radius
              off the ramp. `mt-2` lifts the row off the message: the
              stack's gap-3 sets the rhythm inside the text block, and
              the actions are a tier below it, not another line of it.
              vh-dialog-action-btn carries hover / press / focus. */}
          <div className="flex justify-center gap-3 mt-2">
            <button
              type="button"
              className="vh-dialog-action-btn px-6 py-2.5 min-w-[6.5cqw] text-sm cursor-pointer"
              style={{ ...BEVELED_BTN, borderRadius: ACTION_RADIUS }}
              onClick={onCancel}
            >
              {cancelLabel}
            </button>
            <button
              type="button"
              className="vh-dialog-action-btn px-6 py-2.5 min-w-[6.5cqw] text-sm font-semibold cursor-pointer"
              style={{ ...(danger ? DANGER_BTN : GOLD_BTN), borderRadius: ACTION_RADIUS }}
              onClick={onConfirm}
              autoFocus
            >
              {confirmLabel}
            </button>
          </div>
        </div>
      </div>
    );
  }
}
